// Shared UI helpers for admin manager pages
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash2, GripVertical, ChevronUp, ChevronDown, RefreshCcw, Loader2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { refreshContent } from "@/lib/content";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="text-slate-500 mt-1 text-sm">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function GlassCard({ children, className = "" }) {
  return (
    <div className={`bg-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl shadow-sm ${className}`}>
      {children}
    </div>
  );
}

export function EmptyState({ label, action }) {
  return (
    <div className="border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center bg-white/60">
      <p className="text-slate-500">{label}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Generic Manager component:
 *  - endpoint: `/admin/cms/{name}`
 *  - fields: [{ key, label, type: 'text'|'textarea'|'image'|'switch'|'number', help, required, options? }]
 *  - renderRow: (item) => JSX for list card
 *  - emptyState: label & CTA
 */
export function useCrud(endpoint) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const create = async (payload) => {
    setBusy(true);
    try {
      await api.post(endpoint, payload);
      toast.success("Created");
      await load();
      refreshContent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const update = async (id, payload) => {
    setBusy(true);
    try {
      await api.put(`${endpoint}/${id}`, payload);
      toast.success("Saved");
      await load();
      refreshContent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item permanently?")) return;
    setBusy(true);
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Deleted");
      await load();
      refreshContent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const move = async (id, dir) => {
    const i = items.findIndex(x => x.id === id);
    if (i < 0) return;
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= items.length) return;
    const copy = items.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setItems(copy);
    try {
      await api.post(`${endpoint}/reorder`, { ids: copy.map(x => x.id) });
      refreshContent();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return { items, loading, busy, load, create, update, remove, move };
}

export function ItemDialog({ open, onOpenChange, title, fields, initial = {}, onSubmit, submitting }) {
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial); }, [initial, open]);

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v?.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>Changes appear on the public site immediately after saving.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              <Label htmlFor={f.key} className="text-sm font-semibold">
                {f.label} {f.required && <span className="text-red-500">*</span>}
              </Label>
              {f.type === "textarea" ? (
                <Textarea id={f.key} value={form[f.key] ?? ""} onChange={set(f.key)} rows={f.rows || 3} className="mt-1" data-testid={`field-${f.key}`} />
              ) : f.type === "switch" ? (
                <div className="flex items-center gap-2 mt-1">
                  <Switch checked={!!form[f.key]} onCheckedChange={set(f.key)} data-testid={`field-${f.key}`} />
                  <span className="text-sm text-slate-600">{form[f.key] ? "Enabled" : "Disabled"}</span>
                </div>
              ) : f.type === "number" ? (
                <Input id={f.key} type="number" value={form[f.key] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: Number(e.target.value) }))} className="mt-1" data-testid={`field-${f.key}`} />
              ) : f.type === "image" ? (
                <>
                  <Input id={f.key} value={form[f.key] ?? ""} onChange={set(f.key)} placeholder="Paste image URL (https://...)" className="mt-1" data-testid={`field-${f.key}`} />
                  {form[f.key] && (
                    <div className="mt-2 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                      <img src={form[f.key]} alt="Preview" className="max-h-40 w-auto mx-auto" />
                    </div>
                  )}
                </>
              ) : (
                <Input id={f.key} value={form[f.key] ?? ""} onChange={set(f.key)} placeholder={f.placeholder} className="mt-1" data-testid={`field-${f.key}`} />
              )}
              {f.help && <p className="text-xs text-slate-500 mt-1">{f.help}</p>}
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-[#0057B8] hover:bg-[#003F87] text-white" data-testid="dialog-submit">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RowActions({ onEdit, onDelete, onUp, onDown, canUp, canDown }) {
  return (
    <div className="flex items-center gap-1">
      <button onClick={onUp} disabled={!canUp} className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30" title="Move up"><ChevronUp className="w-4 h-4" /></button>
      <button onClick={onDown} disabled={!canDown} className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30" title="Move down"><ChevronDown className="w-4 h-4" /></button>
      <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600" title="Edit"><Pencil className="w-4 h-4" /></button>
      <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}
